import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";

import { UploadForm } from "../../forms/uploadform/UploadForm";
import { Upload, getUploadCount } from "../../../models/Upload";
import { UploadPageStyles } from "./UploadPage.style";
import { Header } from "../../controls/Header/Header";
import { Footer } from "../../controls/Footer/Footer";
import { UploadStatus, Status } from "../../controls/UploadStatus/UploadStatus";
import { usePostUpload } from "../../../hooks/usePostUpload/usePostUpload";
import { useSnackbar } from "notistack";
import lazyWithPreload from "../../../commons/lazyWithPreload";
import { useGlobalState } from "../../contexts/GlobalStateProvider";

/**
 * Upload page
 */
export const UploadPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = UploadPageStyles();
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(Status.Idle);
  const [files, setFiles] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const { postUpload, postProgress, post } = usePostUpload();
  const { setGlobalState } = useGlobalState();

  const isFinished = (): boolean => {
    return uploading && !postUpload.loading;
  };

  const submit = (data: Upload) => {
    post(data);
    setFiles(getUploadCount(data));
    setStatus(Status.Transmitting);
    setUploading(true);
  };

  const reset = () => {
    setUploading(false);

    // indicate that we want to free all former DICOM images from wado loader
    setGlobalState({ initialized: true, reset: true });
  };

  useEffect(() => {
    const setError = (text: string) => {
      enqueueSnackbar(text, { variant: "error" });
    };
    const setSuccess = (text: string) => {
      enqueueSnackbar(text, { variant: "success" });
    };

    if (postUpload.error && status === Status.WaitingForResponse) {
      setStatus(Status.Error);
      setError(t("upload.post.failed", { status: postUpload.status }));
      return;
    }

    if (postUpload.status && status === Status.WaitingForResponse) {
      if (postUpload.status === 200) {
        setSuccess(t("upload.post.successful"));
        setStatus(Status.Success);
      } else {
        setStatus(Status.Error);
        setError(t("upload.post.failed", { status: postUpload.status }));
      }
    }
  }, [
    postUpload.loading,
    postUpload.status,
    postUpload.error,
    status,
    enqueueSnackbar,
    setStatus,
    t,
  ]);

  useEffect(() => {
    if (
      postProgress.complete > 0 &&
      postProgress.complete === postProgress.total &&
      status === Status.Transmitting
    ) {
      setStatus(Status.WaitingForResponse);
    }
  }, [postProgress.complete, postProgress.total, status, setStatus]);

  useEffect(() => {
    const Step3Lazy = lazyWithPreload(() =>
      import("../../forms/uploadform/ui/Step3Lazy").then(({ Step3Lazy }) => ({
        default: Step3Lazy,
      }))
    );
    Step3Lazy.preload();
  }, []);

  const buttons = () => {
    return isFinished() ? (
      <div>
        <Button
          variant="contained"
          color="primary"
          onClick={reset}
          className={styles.resetButton}
        >
          {t("button.new")}
        </Button>
      </div>
    ) : (
      <div></div>
    );
  };

  const content = () => {
    return !uploading ? (
      <UploadForm submit={submit} />
    ) : (
      <>
        <UploadStatus
          status={status}
          countFiles={files}
          bytesCompleted={postProgress.complete}
          bytesTotal={postProgress.total}
        />
        {buttons()}
      </>
    );
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <Header title={t("title")} />
          {content()}
        </div>
        <Footer showAdmin={!uploading} />
      </div>
    </>
  );
};
